import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useEnabledQuery } from '../../../ui/hooks/useEnabledQuery';
import { setToDirty } from '../../../ui/ui-state/local-status/localStatusActions';
import { ResourceType } from '../../enumerations/ResourceType';
import { get, post } from '../APIVersion1';
import { IndirectlyVersionedArtifactResource } from '../artifactResourceTypes';
import {
    CloneMutateObject,
    CloneMutateObjectSchema,
    CopyActivitiesMutateObject,
    CopyActivitiesMutateObjectSchema,
} from '../mutateObjects';
import { PersistableSchemaType } from '../persistableResourceTypes';
import { ParamConstants } from '../types';
import { useMutationWithDelay } from '../useMutationWithDelay';
import { useResourceAdapterActions } from './useResourceAdapterActions';

export function useIndirectlyVersionedResourceAdapter<R extends IndirectlyVersionedArtifactResource>(
    apiPostfix: string,
    resourceType: ResourceType,
    resourceSchema: PersistableSchemaType<R>
) {
    const actions = useResourceAdapterActions(apiPostfix, resourceType, resourceSchema);
    const getEnabled = useEnabledQuery();

    const useGetResourceByObjectIdAndVersionedIn = (
        objectId: string | undefined | null,
        versionedIn: string | undefined
    ) => {
        return useQuery({
            queryKey: ['VERSIONED_IN', resourceType, objectId, versionedIn],
            queryFn: () =>
                get(`${apiPostfix}/versioned/${objectId}/in/${versionedIn}`).then((result) =>
                    actions.processGetResourceResponse(result)
                ),
            enabled: getEnabled(objectId && versionedIn),
        });
    };

    // TODO use or remove
    const useGetAllResourcesByObjectIdWhereVersionedInIsIn = (
        objectId: string | undefined,
        versionedIn: string[] | undefined
    ) => {
        return useQuery({
            queryKey: ['VERSIONED_IN_IS_IN', resourceType, objectId, versionedIn],
            queryFn: () =>
                get(`${apiPostfix}/all-versioned/${objectId}?${ParamConstants.VERSIONED_IN}=${versionedIn}`).then(
                    (result) => actions.processGetCollectionResponse<R>(result)
                ),
            enabled: getEnabled(objectId && versionedIn),
        });
    };

    const useCloneResource = () => {
        const dispatch = useDispatch();
        return useMutationWithDelay(
            ({ id, names, projectLineVersionId }: CloneMutateObject) => {
                const projectLineVersionParam =
                    projectLineVersionId ? `?project-line-version=${projectLineVersionId}` : '';

                return post(`${apiPostfix}/${id}/clone${projectLineVersionParam}`, JSON.stringify(names)).then(
                    (result) => actions.processMutationResponse(result, 'BATCH_ADD')
                );
            },
            CloneMutateObjectSchema,
            {
                onSettled: (res) => {
                    if (res?.successful) {
                        dispatch(setToDirty(false));
                    }
                },
            }
        );
    };

    const useCopyResources = () => {
        const dispatch = useDispatch();
        return useMutationWithDelay(
            (copyMutateObject: CopyActivitiesMutateObject) => {
                return post(`${apiPostfix}/copy-multiple-from-version`, JSON.stringify(copyMutateObject)).then(
                    (result) => actions.processMutationResponse(result, 'BATCH_ADD')
                );
            },
            CopyActivitiesMutateObjectSchema,
            {
                onSettled: (res) => {
                    if (res?.successful) {
                        dispatch(setToDirty(false));
                    }
                },
            }
        );
    };

    return {
        useGetResourceByObjectIdAndVersionedIn,
        useGetAllResourcesByObjectIdWhereVersionedInIsIn,
        useCloneResource,
        useCopyResources,
    };
}
